import { ReactNode, useState, useEffect, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';

interface ErrorBoundaryProps {
  children: ReactNode;
}

export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [errors, setErrors] = useState<Error[] | null>(null);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setHasError(true);
      setErrors((prev) => [...(prev || []), new Error(event.error.message)]);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      setHasError(true);
      setErrors((prev) => [...(prev || []), new Error(event.reason.message)]);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  // Reset error boundary when children change
  useEffect(() => {
    if (hasError) {
      setHasError(false);
      setErrors(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const showToast = useCallback(() => {
    errors?.forEach((error) => {
      toast.error(error.message, {
        onClose: () => {
          setHasError(false);
          setErrors(null);
        },
      });
    });
  }, [errors]);

  return (
    <>
      {children}

      {hasError && showToast()}

      <ToastContainer
        position='bottom-center'
        autoClose={5000}
        theme='colored'
      />
    </>
  );
};
